<template>
  <div>
    <a-card title="销售记录">
      <a-row gutter="16">
        <a-col :span="24" :md="8" :xl="6" style="max-width: 256px; margin-bottom: 12px;">
          <a-input-search v-model="searchForm.search" placeholder="单号,客户,产品名称/编号" allowClear @search="search"/>
        </a-col>
        <a-col :span="24" :md="8" :xl="6" style="width: 140px; margin-bottom: 12px;">
          <a-button type="primary" icon="printer" :loading="printLoading" @click="printAll">全量打印</a-button>
          <a-button ref="printBtn" v-print="printConfig" v-show="false">打印</a-button>
        </a-col>
      </a-row>

      <a-row style="margin-top: 12px;">
        <a-table size="small" :columns="columns" :dataSource="items" rowKey="id" :loading="loading"
                 :pagination="pagination"
                 @change="tableChange">
          <template slot="expandedRowRender" slot-scope="record">
            <a-table size="small" :columns="goodsColumns" :dataSource="record.sales_goods_items || []" rowKey="id"
              :pagination="false" />
          </template>
          <div slot="action" slot-scope="value, item">
            <a-button-group size="small">
              <a-button size="small" @click="detial(item)">详情</a-button>
              <a-popconfirm title="确定作废吗" @confirm="voidItem(item)">
              <a-button type="danger" :disabled="item.is_void">{{ item.is_void ? '已作废' : '作废'}}</a-button>
              </a-popconfirm>
            </a-button-group>
          </div>
        </a-table>
      </a-row>
    </a-card>

    <!-- 全量打印内容：移出屏幕但仍参与布局，避免 display:none 导致只能打出一页 -->
    <div style="position: absolute; left: -99999px; top: 0; width: 210mm;">
    <div id="printSaleAll" style="padding: 8px;">
      <div style="text-align: center; font-size: 18px; font-weight: bold; margin: 4px 0;">销售记录</div>
      <div v-if="printFilterText" style="font-size: 12px; margin-bottom: 6px;">筛选条件: {{ printFilterText }}</div>
      <table border="1" style="border-collapse: collapse; width: 100%; font-size: 12px;">
        <thead>
          <tr>
            <th style="padding: 3px;">销售时间</th>
            <th style="padding: 3px;">客户</th>
            <th style="padding: 3px;">产品名称</th>
            <th style="padding: 3px;">规格</th>
            <th style="padding: 3px;">销售数量</th>
            <th style="padding: 3px;">价格</th>
            <th style="padding: 3px;">总金额</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(r, i) in printRows" :key="i">
            <td style="padding: 3px; white-space: nowrap;">{{ r.time }}</td>
            <td style="padding: 3px;">{{ r.party }}</td>
            <td style="padding: 3px;">{{ r.name }}</td>
            <td style="padding: 3px;">{{ r.spec }}</td>
            <td style="padding: 3px; text-align: right;">{{ r.quantity }}</td>
            <td style="padding: 3px; text-align: right;">{{ r.price }}</td>
            <td style="padding: 3px; text-align: right;">{{ r.amount }}</td>
          </tr>
          <tr>
            <td colspan="4" style="padding: 3px; text-align: right; font-weight: bold;">合计</td>
            <td style="padding: 3px; text-align: right; font-weight: bold;">{{ printTotalQuantity }}</td>
            <td></td>
            <td style="padding: 3px; text-align: right; font-weight: bold;">{{ printTotalAmount }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  </div>
</template>

<script>
import {saleOrderList, saleOrdersVoid} from '@/api/sale'
import {fetchAllPages} from '@/utils/fetchAll'
import {formatQtyWithUnit, joinPackQtyDisplay, toPackPrice} from '@/utils/pack'
import NP from 'number-precision'

export default {
  name: 'SaleRecord',
  components: {},
  data() {
    return {
      columns: [
        {
          title: '序号',
          dataIndex: 'index',
          key: 'index',
          customRender: (value, item, index) => {
            return index + 1
          },
          width: 45
        },
        {
          title: '销售编号',
          dataIndex: 'number',
          sorter: true,
          width: 160
        },
        {
          title: '客户',
          dataIndex: 'client_name',
          width: 160
        },
        {
          title: '经手人',
          dataIndex: 'handler_name',
          width: 160
        },
        {
          title: '处理日期',
          dataIndex: 'handle_time',
          width: 150
        },
        {
          title: '销售总数量',
          dataIndex: 'total_quantity',
          width: 140,
          customRender: (value, item) => {
            const goods = (item && item.sales_goods_items) || [];
            if (goods.length) {
              return joinPackQtyDisplay(goods.map((g) => ({ ...g, qty: g.sales_quantity })));
            }
            return formatQtyWithUnit(value, item) || value;
          },
        },
        {
          title: '开单金额',
          dataIndex: 'order_amount',
          width: 110,
          customRender: (value, item) => (value != null ? value : item.total_amount),
        },
        {
          title: '结算金额',
          dataIndex: 'total_amount',
          width: 110
        },
        {
          title: '收款金额',
          dataIndex: 'collection_amount',
          width: 120
        },
        {
          title: '其他费用',
          dataIndex: 'other_amount',
          width: 120
        },
        {
          title: '状态',
          dataIndex: 'status',
          customRender: (value, item, index) => {
            if (item.total_amount === item.collection_amount) {
              return '现结';
            } else {
              const owedAmount = item.total_amount - item.collection_amount;
              return `欠债 ${owedAmount}`;
            }
          },
          width: 150
        },
        {
          title: '操作',
          dataIndex: 'action',
          scopedSlots: {customRender: 'action'},
          width: 200
        },
      ],
      goodsColumns: [
        { title: '产品名称', dataIndex: 'goods_name' },
        { title: '产品编号', dataIndex: 'goods_number' },
        { title: '规格', dataIndex: 'goods_spec', width: 180 },
        {
          title: '销售数量',
          dataIndex: 'sales_quantity',
          width: 140,
          customRender: (v, item) => formatQtyWithUnit(v, item),
        },
        {
          title: '开单价',
          dataIndex: 'sales_price',
          width: 100,
          customRender: (v, item) => {
            const p = toPackPrice(v, item);
            return p != null ? p : v;
          },
        },
        { title: '开单金额', dataIndex: 'total_amount', width: 100 },
        {
          title: '结算单价',
          dataIndex: 'settle_price',
          width: 100,
          customRender: (v, item) => {
            const p = toPackPrice(v != null ? v : item.sales_price, item);
            return p != null ? p : v;
          },
        },
        {
          title: '结算金额',
          dataIndex: 'settle_amount',
          width: 100,
          customRender: (v, item) => (v != null ? v : item.total_amount),
        },
      ],
      searchForm: {page: 1, page_size: 16},
      pagination: {current: 1, total: 0, pageSize: 16},
      loading: false,
      items: [],
      visible: false,
      targetItem: {},
      form: {},
      printRows: [],
      printTotalQuantity: '',
      printTotalAmount: 0,
      printFilterText: '',
      printLoading: false,
      printConfig: {
        id: 'printSaleAll',
        popTitle: '销售记录',
        extraHead: '<style>@page{size:auto;margin:8mm}html,body{height:auto!important;overflow:visible!important}#printSaleAll{height:auto!important;overflow:visible!important}table{width:100%;border-collapse:collapse;page-break-inside:auto}tr{page-break-inside:avoid;page-break-after:auto}thead{display:table-header-group}tfoot{display:table-footer-group}</style>',
      },
    };
  },
  computed: {},
  methods: {
    initialize() {
      this.list();
    },
    list() {
      this.loading = true;
      saleOrderList(this.searchForm).then(data => {
        this.pagination.total = data.count;
        this.items = data.results;
      }).finally(() => {
        this.loading = false;
      });
    },
    tableChange(pagination, filters, sorter) {
      this.searchForm.page = pagination.current;
      this.pagination.current = pagination.current;
      this.searchForm.ordering = `${sorter.order == 'descend' ? '-' : ''}${sorter.field}`;
      this.list();
    },
    onChangePicker(date, dateString) {
      let startDate = date[0], endDate = date[1];
      this.searchForm.start_date = startDate ? startDate.format('YYYY-MM-DD') : undefined;
      this.searchForm.end_date = endDate ? endDate.add(1, 'days').format('YYYY-MM-DD') : undefined;
      this.search();
    },
    search() {
      this.searchForm.page = 1;
      this.pagination.current = 1;
      this.list();
    },
    detial(item) {
      this.$router.push({path: '/sale/sale_record_detail', query: {id: item.id}});
    },
    voidItem(item) {
      saleOrdersVoid({id: item.id}).then(() => {
        this.$message.success('作废成功');
        this.list();
      });
    },
    async printAll() {
      this.printLoading = true;
      try {
        // 后端默认每页 16 条，需翻页取完全部结果后再打印
        const orders = await fetchAllPages(saleOrderList, this.searchForm, 9999);
        const keyword = (this.searchForm.search || '').trim().toLowerCase();
        const rows = [];
        const qtyParts = [];
        let totalAmount = 0;
        orders.forEach(order => {
          let goodsItems = order.sales_goods_items || [];
          if (keyword) {
            const matched = goodsItems.filter(g =>
              `${g.goods_name || ''}${g.goods_number || ''}${g.goods_spec || ''}`.toLowerCase().includes(keyword));
            if (matched.length) goodsItems = matched;
          }
          goodsItems.forEach(g => {
            rows.push({
              time: order.handle_time,
              party: order.client_name,
              name: g.goods_name,
              spec: g.goods_spec,
              quantity: formatQtyWithUnit(g.sales_quantity, g),
              price: toPackPrice(g.sales_price, g),
              amount: g.total_amount,
            });
            qtyParts.push({ qty: g.sales_quantity, unit_name: g.unit_name });
            totalAmount = NP.plus(totalAmount, Number(g.total_amount) || 0);
          });
        });
        this.printRows = rows;
        this.printTotalQuantity = joinPackQtyDisplay(qtyParts);
        this.printTotalAmount = totalAmount;
        this.printFilterText = this.searchForm.search || '';
        if (!rows.length) {
          this.$message.warning('暂无可打印的数据');
          return;
        }
        await this.$nextTick();
        // 大数据量时稍等 DOM 渲染完成再触发打印
        setTimeout(() => {
          this.$refs.printBtn && this.$refs.printBtn.$el.click();
        }, 100);
      } catch (e) {
        this.$message.error('获取打印数据失败');
      } finally {
        this.printLoading = false;
      }
    },
  },
  mounted() {
    this.initialize();
  },
}
</script>
